import {
	BLOCK_TYPES,
	SiteEcommerceDynamicProductPage,
	SitePage,
} from '@hostinger/builder-schema-validator/schema/schemaTypes';
import {
	EcommerceProduct,
	EcommerceProductType,
	GOOGLE_TAG_MANAGER_EVENT,
} from '@zyro-inc/site-modules/types';
import { analyticsEvent } from '@zyro-inc/site-modules/utils/analyticsEvent';
import EventLogApi from '@zyro-inc/site-modules/api/EventLogApi';

import { useEcommerce } from '@zyro-inc/site-modules/components/ecommerce/useEcommerce';
import { useEcommerceGlobal } from '@zyro-inc/site-modules/use/useEcommerceGlobal';
import { useEcommerceModal } from '@zyro-inc/site-modules/components/ecommerce/modals/useEcommerceModal';
import { useSiteGlobal } from '@zyro-inc/site-modules/use/useSiteGlobal';
import { useCookieBanner } from '@zyro-inc/site-modules/utils/useCookieBanner';
import { getAddToCartEventPayload } from '@zyro-inc/site-modules/utils/googleTagManager';

export const useAddProduct = ({ blockId }: { blockId?: string }) => {
	const {
		openEcommerceModal,
		closeEcommerceModal,
		setProductPreviewData,
	} = useEcommerceModal();

	const {
		legacyProductPages,
		isShoppingCartOpen,
		shoppingCartItems,
		setShoppingCartOpen,
		setShoppingCartItems,
		setIsCheckoutLoading,
		setSelectedBookingProduct,
		productPageTemplates,
		defaultProductPageTemplate,
	} = useEcommerceGlobal({
		blockId,
	});

	const { initiateCheckout } = useEcommerce();

	const {
		meta,
		blocks,
		isDynamicProductPageEnabled,
		hasAnalyticsIntegration,
	} = useSiteGlobal();

	const { areCookiesAllowed } = useCookieBanner(meta);

	const openVariantsModal = (product: EcommerceProduct) => {
		const template = productPageTemplates.value[product.id] || defaultProductPageTemplate.value;

		const itemProductPage: SitePage | undefined = Object.values(legacyProductPages.value)
			.find((page: SitePage) => (page as SiteEcommerceDynamicProductPage).productId === product.id);
		const ecommerceBlocks = Object.keys(blocks.value).filter((key) => blocks.value[key].type === BLOCK_TYPES.BLOCK_ECOMMERCE_PRODUCT);

		const pageProductBlockId = isDynamicProductPageEnabled.value
			? ecommerceBlocks.find((block) => template.blocks?.includes(block))
			: ecommerceBlocks.find((block) => itemProductPage?.blocks?.includes(block));

		if (!pageProductBlockId) {
			if (!product?.seo_settings?.slug) {
				window.location.assign('/');

				return;
			}

			window.location.assign(`/${product.seo_settings.slug}`);

			return;
		}

		setShoppingCartOpen(false);
		setProductPreviewData(blocks.value[pageProductBlockId], String(product.id));
		openEcommerceModal('EcommerceProductPreview');
	};

	const addProductToBag = async ({
		product,
		isInPreviewMode,
		isCartVisible,
		location,
	}: {
        product: EcommerceProduct,
        isInPreviewMode: boolean,
        isCartVisible: boolean,
		location?: string,
    }) => {
		if (isInPreviewMode) {
			openEcommerceModal('EcommerceMessageButtonDisabled');

			return;
		}

		const hasMandatoryCustomFields = product.custom_fields?.some((field) => field.is_required);

		if (product.options.length || hasMandatoryCustomFields) {
			openVariantsModal(product);

			return;
		}

		EventLogApi.logEvent({
			eventName: 'builder_customers.ecomm_add_to_cart.enter',
			eventProperties: {
				product_type: product.type.value,
				...(location && {
					location,
				}),
			},
		});

		const productForShoppingCart = [
			{
				...product,
				variants: [product.variants[0]],
			},
		];

		await closeEcommerceModal();

		if (product.type.value === EcommerceProductType.BOOKING) {
			setSelectedBookingProduct(product);
			openEcommerceModal('EcommerceBookingEventSelect');

			return;
		}

		if (product.type.value === EcommerceProductType.DIGITAL) {
			const isProductAlreadyInCart = shoppingCartItems.value.some((item) => item.id === product.id);

			if (isProductAlreadyInCart) {
				setShoppingCartOpen(true);

				return;
			}
		}

		if (isCartVisible) {
			setShoppingCartItems([
				...shoppingCartItems.value,
				...productForShoppingCart,
			]);

			if (areCookiesAllowed.value && hasAnalyticsIntegration.value) {
				analyticsEvent(GOOGLE_TAG_MANAGER_EVENT.ADD_TO_CART, getAddToCartEventPayload(productForShoppingCart[0]));
			}

			if (isShoppingCartOpen.value) {
				return;
			}

			setShoppingCartOpen(true);
		} else {
			setIsCheckoutLoading(true);
			await initiateCheckout(productForShoppingCart).then(() => {
				setIsCheckoutLoading(false);
			});
		}
	};

	return {
		addProductToBag,
		openVariantsModal,
	};
};
